import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from './Navigation';
import { Bot, Copy, Check, ChevronDown, ChevronUp, Loader } from 'lucide-react';

// Import components directly from the source files
import LoadingSpinner from './LoadingSpinner';
import Pagination from './Pagination';
import ContactButton from './ContactButton';
import ContactModal from './ContactModal';
import YouTubeEmbed from './YouTubeEmbed';
import CollapsibleFAQ from './CollapsibleFAQ';
import DynamicCTA from './DynamicCTA';
import SearchBox from './SearchBox';
import BlogTimeline from './BlogTimeline';
import ImageSelector from './ImageSelector';
import VideoSelector from './VideoSelectorModal';
import CalWidget from './CalWidget';

// Define the ContactFormData interface
interface ContactFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

export default function ComponentLibraryDemo() {
  const [currentPage, setCurrentPage] = useState(1);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isImageSelectorOpen, setIsImageSelectorOpen] = useState(false);
  const [isVideoSelectorOpen, setIsVideoSelectorOpen] = useState(false);
  const [isCalWidgetOpen, setIsCalWidgetOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'basic': true,
    'media': true,
    'layout': true,
    'interactive': true
  });
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Example FAQ items
  const faqs = [
    {
      question: "What is a component library?",
      answer: "A component library is a collection of reusable UI components that can be shared across multiple projects to maintain consistency and reduce development time."
    },
    {
      question: "Why use a component library?",
      answer: "Using a component library helps maintain design consistency, speeds up development, reduces code duplication, and makes it easier to update UI elements across multiple projects."
    },
    {
      question: "How do I contribute to the component library?",
      answer: "You can contribute by adding new components, improving existing ones, writing documentation, or fixing bugs. Make sure to follow the project's contribution guidelines."
    }
  ];

  // Example blog posts for timeline
  const examplePosts = [
    {
      id: '1',
      title: 'Getting Started with Component Libraries',
      slug: 'getting-started-component-libraries',
      image_url: 'https://images.unsplash.com/photo-1591453089816-0fbb971b454c',
      published_at: new Date().toISOString(),
      category: {
        name: 'Development',
        slug: 'development'
      }
    },
    {
      id: '2',
      title: 'Best Practices for Reusable Components',
      slug: 'best-practices-reusable-components',
      image_url: 'https://images.unsplash.com/photo-1677442135968-6d89485dd1d1',
      published_at: new Date(Date.now() - 86400000).toISOString(), // Yesterday
      category: {
        name: 'Design',
        slug: 'design'
      }
    },
    {
      id: '3',
      title: 'Creating a Unified Design System',
      slug: 'unified-design-system',
      image_url: 'https://images.unsplash.com/photo-1552664730-d307ca884978',
      published_at: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
      category: {
        name: 'UI/UX',
        slug: 'ui-ux'
      }
    }
  ];

  // Example search function
  const handleSearch = async (query: string): Promise<any[]> => {
    // Simulate API call
    return new Promise((resolve) => {
      setTimeout(() => {
        const results = [
          {
            id: '1',
            title: `Search result for "${query}"`,
            description: 'This is a sample search result',
            url: '#',
            type: 'Article'
          },
          {
            id: '2',
            title: `Another result for "${query}"`,
            description: 'This is another sample search result',
            url: '#',
            type: 'Course'
          }
        ];
        resolve(results);
      }, 500);
    });
  };

  const toggleLoading = () => {
    setIsLoading(!isLoading);
  };

  const handleImageSelect = (url: string) => {
    setSelectedImage(url);
    setIsImageSelectorOpen(false);
  };

  const handleVideoSelect = (url: string) => {
    setSelectedVideo(url);
    setIsVideoSelectorOpen(false);
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const copyToClipboard = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Component code examples
  const codeExamples = {
    loadingSpinner: `import { LoadingSpinner } from '@one80labs/ui';

<LoadingSpinner size="lg" />`,
    pagination: `import { Pagination } from '@one80labs/ui';

<Pagination 
  currentPage={currentPage}
  totalPages={5}
  onPageChange={setCurrentPage}
/>`,
    searchBox: `import { SearchBox } from '@one80labs/ui';

<SearchBox 
  onSearch={handleSearch}
  placeholder="Search for components..."
/>`,
    youtubeEmbed: `import { YouTubeEmbed } from '@one80labs/ui';

<YouTubeEmbed 
  videoId="WjHWcaLFgeI"
  title="AI Business Transformation"
/>`,
    faq: `import { CollapsibleFAQ } from '@one80labs/ui';

<CollapsibleFAQ 
  faqs={[
    {
      question: "What is a component library?",
      answer: "A collection of reusable UI components."
    }
  ]}
  title="Frequently Asked Questions"
/>`,
    dynamicCTA: `import { DynamicCTA } from '@one80labs/ui';

<DynamicCTA 
  type="ai_training"
  onContactClick={() => setIsContactModalOpen(true)}
  onCalendarClick={() => alert('Calendar clicked')}
/>`,
    blogTimeline: `import { BlogTimeline } from '@one80labs/ui';

<BlogTimeline 
  posts={posts}
  onCategoryClick={(slug) => navigate(\`/category/\${slug}\`)}
  onPostClick={(slug) => navigate(\`/post/\${slug}\`)}
/>`,
    contactButton: `import { ContactButton } from '@one80labs/ui';

<ContactButton 
  onClick={() => setIsContactModalOpen(true)}
  onCalendarClick={() => setIsBookingOpen(true)}
  onCalClick={() => setIsCalOpen(true)}
/>`,
    contactModal: `import { ContactModal, ContactFormData } from '@one80labs/ui';

<ContactModal 
  isOpen={isContactModalOpen}
  onClose={() => setIsContactModalOpen(false)}
  onSubmit={async (data: ContactFormData) => {
    // Handle form submission
    return true;
  }}
/>`,
    calWidget: `import { CalWidget } from '@one80labs/ui';

<CalWidget 
  isOpen={isCalWidgetOpen}
  onClose={() => setIsCalWidgetOpen(false)}
  calLink="one80/30min"
/>`,
    imageSelector: `import { ImageSelector } from '@one80labs/ui';

<ImageSelector
  isOpen={isImageSelectorOpen}
  onClose={() => setIsImageSelectorOpen(false)}
  onSelect={handleImageSelect}
/>`,
    videoSelector: `import { VideoSelector } from '@one80labs/ui';

<VideoSelector
  isOpen={isVideoSelectorOpen}
  onClose={() => setIsVideoSelectorOpen(false)}
  onSelect={handleVideoSelect}
/>`
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <Link to="/" className="text-[#F98B3D] hover:text-[#e07a2c]">
            &larr; Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">One80Labs Component Library</h1>
          <p className="text-gray-600 mt-2">
            This page demonstrates the reusable components from our UI library that can be shared across multiple projects.
          </p>
        </div>
        
        {isLoading && <LoadingSpinner size="lg" />}
        
        <div className="space-y-8">
          {/* Basic Components Section */}
          <section className="border border-gray-200 rounded-lg overflow-hidden">
            <button 
              className="w-full flex items-center justify-between bg-gray-50 px-6 py-4 text-left"
              onClick={() => toggleSection('basic')}
            >
              <h2 className="text-xl font-semibold text-gray-900">Basic Components</h2>
              {expandedSections.basic ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>
            
            {expandedSections.basic && (
              <div className="p-6 space-y-8">
                {/* Loading Spinner */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">Loading Spinner</h3>
                    <button
                      onClick={() => copyToClipboard(codeExamples.loadingSpinner, 'loadingSpinner')}
                      className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                    >
                      {copiedCode === 'loadingSpinner' ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <button
                      onClick={toggleLoading}
                      className="px-4 py-2 bg-[#F98B3D] text-white rounded-md hover:bg-[#e07a2c]"
                    >
                      {isLoading ? 'Hide' : 'Show'} Loading Spinner
                    </button>
                  </div>
                  <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto text-sm">
                    {codeExamples.loadingSpinner}
                  </pre>
                </div>

                {/* Pagination */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">Pagination</h3>
                    <button
                      onClick={() => copyToClipboard(codeExamples.pagination, 'pagination')}
                      className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                    >
                      {copiedCode === 'pagination' ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-600 mb-4">Current Page: {currentPage}</p>
                    <Pagination 
                      currentPage={currentPage}
                      totalPages={5}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                  <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto text-sm">
                    {codeExamples.pagination}
                  </pre>
                </div>

                {/* SearchBox */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">SearchBox</h3>
                    <button
                      onClick={() => copyToClipboard(codeExamples.searchBox, 'searchBox')}
                      className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                    >
                      {copiedCode === 'searchBox' ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <SearchBox 
                      onSearch={handleSearch}
                      placeholder="Search for components..."
                    />
                  </div>
                  <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto text-sm">
                    {codeExamples.searchBox}
                  </pre>
                </div>
              </div>
            )}
          </section>

          {/* Media Components Section */}
          <section className="border border-gray-200 rounded-lg overflow-hidden">
            <button 
              className="w-full flex items-center justify-between bg-gray-50 px-6 py-4 text-left"
              onClick={() => toggleSection('media')}
            >
              <h2 className="text-xl font-semibold text-gray-900">Media Components</h2>
              {expandedSections.media ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>
            
            {expandedSections.media && (
              <div className="p-6 space-y-8">
                {/* YouTube Embed */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">YouTube Embed</h3>
                    <button
                      onClick={() => copyToClipboard(codeExamples.youtubeEmbed, 'youtubeEmbed')}
                      className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                    >
                      {copiedCode === 'youtubeEmbed' ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <YouTubeEmbed 
                      videoId="WjHWcaLFgeI"
                      title="AI Business Transformation"
                    />
                  </div>
                  <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto text-sm">
                    {codeExamples.youtubeEmbed}
                  </pre>
                </div>

                {/* Image Selector */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">Image Selector</h3>
                    <button
                      onClick={() => copyToClipboard(codeExamples.imageSelector, 'imageSelector')}
                      className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                    >
                      {copiedCode === 'imageSelector' ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <button
                      onClick={() => setIsImageSelectorOpen(true)}
                      className="px-4 py-2 bg-[#F98B3D] text-white rounded-md hover:bg-[#e07a2c]"
                    >
                      Open Image Selector
                    </button>
                    {selectedImage && (
                      <div className="mt-4">
                        <p className="text-gray-600 mb-2">Selected Image:</p>
                        <img 
                          src={selectedImage} 
                          alt="Selected" 
                          className="w-full max-w-md h-auto rounded-lg"
                        />
                      </div>
                    )}
                  </div>
                  <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto text-sm">
                    {codeExamples.imageSelector}
                  </pre>
                </div>

                {/* Video Selector */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">Video Selector</h3>
                    <button
                      onClick={() => copyToClipboard(codeExamples.videoSelector, 'videoSelector')}
                      className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                    >
                      {copiedCode === 'videoSelector' ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <button
                      onClick={() => setIsVideoSelectorOpen(true)}
                      className="px-4 py-2 bg-[#F98B3D] text-white rounded-md hover:bg-[#e07a2c]"
                    >
                      Open Video Selector
                    </button>
                    {selectedVideo && (
                      <div className="mt-4">
                        <p className="text-gray-600 mb-2">Selected Video URL:</p>
                        <div className="bg-gray-100 p-2 rounded">
                          <code>{selectedVideo}</code>
                        </div>
                      </div>
                    )}
                  </div>
                  <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto text-sm">
                    {codeExamples.videoSelector}
                  </pre>
                </div>
              </div>
            )}
          </section>

          {/* Layout Components Section */}
          <section className="border border-gray-200 rounded-lg overflow-hidden">
            <button 
              className="w-full flex items-center justify-between bg-gray-50 px-6 py-4 text-left"
              onClick={() => toggleSection('layout')}
            >
              <h2 className="text-xl font-semibold text-gray-900">Layout Components</h2>
              {expandedSections.layout ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>
            
            {expandedSections.layout && (
              <div className="p-6 space-y-8">
                {/* FAQ Component */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">Collapsible FAQ</h3>
                    <button
                      onClick={() => copyToClipboard(codeExamples.faq, 'faq')}
                      className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                    >
                      {copiedCode === 'faq' ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <CollapsibleFAQ 
                      faqs={faqs}
                      title="Component Library FAQs"
                    />
                  </div>
                  <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto text-sm">
                    {codeExamples.faq}
                  </pre>
                </div>

                {/* Dynamic CTA */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">Dynamic CTA</h3>
                    <button
                      onClick={() => copyToClipboard(codeExamples.dynamicCTA, 'dynamicCTA')}
                      className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                    >
                      {copiedCode === 'dynamicCTA' ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <DynamicCTA 
                      type="ai_training"
                      onContactClick={() => setIsContactModalOpen(true)}
                      onCalendarClick={() => alert('Calendar clicked')}
                    />
                  </div>
                  <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto text-sm">
                    {codeExamples.dynamicCTA}
                  </pre>
                </div>

                {/* Blog Timeline */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">Blog Timeline</h3>
                    <button
                      onClick={() => copyToClipboard(codeExamples.blogTimeline, 'blogTimeline')}
                      className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                    >
                      {copiedCode === 'blogTimeline' ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <BlogTimeline 
                      posts={examplePosts}
                      onCategoryClick={(slug) => alert(`Category clicked: ${slug}`)}
                      onPostClick={(slug) => alert(`Post clicked: ${slug}`)}
                    />
                  </div>
                  <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto text-sm">
                    {codeExamples.blogTimeline}
                  </pre>
                </div>
              </div>
            )}
          </section>

          {/* Interactive Components Section */}
          <section className="border border-gray-200 rounded-lg overflow-hidden">
            <button 
              className="w-full flex items-center justify-between bg-gray-50 px-6 py-4 text-left"
              onClick={() => toggleSection('interactive')}
            >
              <h2 className="text-xl font-semibold text-gray-900">Interactive Components</h2>
              {expandedSections.interactive ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>
            
            {expandedSections.interactive && (
              <div className="p-6 space-y-8">
                {/* Contact Button */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">Contact Button</h3>
                    <button
                      onClick={() => copyToClipboard(codeExamples.contactButton, 'contactButton')}
                      className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                    >
                      {copiedCode === 'contactButton' ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-600 mb-4">The contact button is fixed to the bottom right of the screen. Look for the orange button with a message icon.</p>
                  </div>
                  <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto text-sm">
                    {codeExamples.contactButton}
                  </pre>
                </div>

                {/* Contact Modal */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">Contact Modal</h3>
                    <button
                      onClick={() => copyToClipboard(codeExamples.contactModal, 'contactModal')}
                      className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                    >
                      {copiedCode === 'contactModal' ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <button
                      onClick={() => setIsContactModalOpen(true)}
                      className="px-4 py-2 bg-[#F98B3D] text-white rounded-md hover:bg-[#e07a2c]"
                    >
                      Open Contact Modal
                    </button>
                  </div>
                  <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto text-sm">
                    {codeExamples.contactModal}
                  </pre>
                </div>

                {/* Cal Widget */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">Cal.com Widget</h3>
                    <button
                      onClick={() => copyToClipboard(codeExamples.calWidget, 'calWidget')}
                      className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                    >
                      {copiedCode === 'calWidget' ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <button
                      onClick={() => setIsCalWidgetOpen(true)}
                      className="px-4 py-2 bg-[#F98B3D] text-white rounded-md hover:bg-[#e07a2c]"
                    >
                      Open Cal.com Widget
                    </button>
                  </div>
                  <pre className="bg-gray-800 text-white p-4 rounded-lg overflow-x-auto text-sm">
                    {codeExamples.calWidget}
                  </pre>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
      
      {/* Contact Button (fixed position) */}
      <ContactButton 
        onClick={() => setIsContactModalOpen(true)}
        onCalendarClick={() => alert('Calendar clicked')}
        onCalClick={() => setIsCalWidgetOpen(true)}
      />

      {/* Modals */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSubmit={async (formData: ContactFormData) => {
          try {
            console.log('Form submitted:', formData);
            // In a real implementation, you would call sendContactFormEmail here
            // await sendContactFormEmail(formData);
            return true;
          } catch (error) {
            console.error('Error submitting contact form:', error);
            return false;
          }
        }}
      />

      <ImageSelector
        isOpen={isImageSelectorOpen}
        onClose={() => setIsImageSelectorOpen(false)}
        onSelect={handleImageSelect}
      />

      <VideoSelectorModal
        isOpen={isVideoSelectorOpen}
        onClose={() => setIsVideoSelectorOpen(false)}
        onSelect={(videoHtml) => {
          setSelectedVideo(videoHtml);
          setIsVideoSelectorOpen(false);
        }}
      />

      <CalWidget
        isOpen={isCalWidgetOpen}
        onClose={() => setIsCalWidgetOpen(false)}
        calLink="one80/30min"
      />
      
      <footer className="bg-gray-50 border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex items-center">
              <Bot className="h-6 w-6 text-gray-400" />
              <span className="ml-2 text-gray-500 font-medium">One80Labs UI</span>
            </div>
            <div className="mt-8 md:mt-0">
              <p className="text-center text-sm text-gray-500">
                &copy; {new Date().getFullYear()} One80Labs. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}